import {
    addDays,
    startOfDay,
    startOfWeek,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subWeeks,
    subMonths,
    subQuarters,
    subYears,
} from "date-fns";

// TODO Extra options? "LAST_7DAYS", "LAST_14DAYS", "LAST_30DAYS", "LAST_90DAYS"
export enum RelativePeriods {
    TODAY = "TODAY",
    YESTERDAY = "YESTERDAY",
    THIS_WEEK = "THIS_WEEK",
    LAST_WEEK = "LAST_WEEK",
    THIS_MONTH = "THIS_MONTH",
    LAST_MONTH = "LAST_MONTH",
    THIS_QUARTER = "THIS_QUARTER",
    LAST_QUARTER = "LAST_QUARTER",
    THIS_YEAR = "THIS_YEAR",
    LAST_YEAR = "LAST_YEAR",
    DAY_ONE = "DAY_ONE",
    USER_DEFINED = "USER_DEFINED",
}

export type RelativePeriodStrings = keyof typeof RelativePeriods;

const WEEK_OPTIONS = { weekStartsOn: 1 as const };

export function parseRelativePeriod(
    period: RelativePeriodStrings,
    date?: Date
): { fromDate: Date; toDate: Date } {
    // Plain local Dates on purpose: periods follow the calendar the user sees, so
    // every boundary is a local midnight. Wrapping "now" in UTCDate would make
    // "today" the UTC date, which is yesterday's date just after local midnight.
    const now = date ?? new Date();
    const tomorrow = addDays(now, 1);

    let fromDate: Date = now,
        toDate: Date = now;

    switch (period) {
        case RelativePeriods.TODAY: {
            fromDate = startOfDay(now);
            toDate = startOfDay(tomorrow);
            break;
        }
        case RelativePeriods.YESTERDAY: {
            const yesterday = addDays(now, -1);
            fromDate = startOfDay(yesterday);
            toDate = startOfDay(now);
            break;
        }
        case RelativePeriods.THIS_WEEK: {
            fromDate = startOfWeek(now, WEEK_OPTIONS);
            toDate = startOfDay(tomorrow);
            break;
        }
        case RelativePeriods.LAST_WEEK: {
            const lastWeek = subWeeks(now, 1);
            fromDate = startOfWeek(lastWeek, WEEK_OPTIONS);
            toDate = startOfWeek(now, WEEK_OPTIONS);
            break;
        }
        case RelativePeriods.THIS_MONTH: {
            fromDate = startOfMonth(now);
            toDate = startOfDay(tomorrow);
            break;
        }
        case RelativePeriods.LAST_MONTH: {
            const lastMonth = subMonths(now, 1);
            fromDate = startOfMonth(lastMonth);
            toDate = startOfMonth(now);
            break;
        }
        case RelativePeriods.THIS_QUARTER: {
            fromDate = startOfQuarter(now);
            toDate = startOfDay(tomorrow);
            break;
        }
        case RelativePeriods.LAST_QUARTER: {
            const lastQuarter = subQuarters(now, 1);
            fromDate = startOfQuarter(lastQuarter);
            toDate = startOfQuarter(now);
            break;
        }
        case RelativePeriods.THIS_YEAR: {
            fromDate = startOfYear(now);
            toDate = startOfDay(tomorrow);
            break;
        }
        case RelativePeriods.LAST_YEAR: {
            const lastYear = subYears(now, 1);
            fromDate = startOfYear(lastYear);
            toDate = startOfYear(now);
            break;
        }
        case RelativePeriods.DAY_ONE: {
            // Approx the date of the first release of Venue POS - 2020-12-01
            fromDate = new Date(2020, 11, 1);
            toDate = startOfDay(tomorrow);
            break;
        }
        case RelativePeriods.USER_DEFINED:
        default: {
            break;
        }
    }

    return { fromDate, toDate };
}
